import {
  HttpEventType,
  HttpProgressEvent,
  HttpResponse,
  HttpSentEvent,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { isWSStatusErrorResponse } from '@qtek/core/websockets-core';
import {
  DocNode,
  DOCUMENT_UPLOAD_CANCELED,
  DOCUMENT_UPLOAD_COMPLETED,
  DOCUMENT_UPLOAD_STARTED,
  PreUploadState,
} from '@qtek/shared/models';
import {
  catchError,
  filter,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { AppState } from '..';
import { selectCurrentPerson } from '../user';
import { DocumentActions } from './documents.actions';
import { selectPreUploadDocuments } from './documents.reducer';
import { DocumentsService } from './documents.service';

@Injectable()
export class DocumentsEffects {
  connect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.connectDocumentsList),
      switchMap(({ payload }: any) =>
        this.documentsService.subscribe({ prms: payload }).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(DocumentActions.disconnectDocumentsList),
              filter((action: any) => payload?.mysid === action.payload?.mysid)
            )
          )
        )
      ),
      map(({ op, res, sts }: any) => {
        switch (op) {
          case 'query':
            return isWSStatusErrorResponse(sts)
              ? DocumentActions.getEntityDocumentsFailure()
              : DocumentActions.getEntityDocumentsWSSuccess({
                  payload: res,
                  meta: sts?.meta || null,
                });
          case 'ins': {
            if (res) {
              if (res.lastToken) {
                this.store.dispatch(
                  DocumentActions.generatePlaceholderTokenSuccess({
                    payload: { tkn: res.lastToken },
                  })
                );
              }
              return DocumentActions.uploadDocumentsWSSuccess({ payload: res });
            } else {
              return DocumentActions.uploadDocumentsWSFailure({ payload: sts });
            }
          }

          case 'del':
            return res
              ? DocumentActions.deleteDocumentWSSuccess({ payload: res })
              : DocumentActions.deleteDocumentWSFailure({ payload: sts });
          case 'upd':
            return res
              ? DocumentActions.updateDocumentWSSuccess({ payload: res })
              : DocumentActions.updateDocumentWSFailure({ payload: sts });
          default:
            return { type: 'NONE' };
        }
      })
    )
  );

  update$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentActions.updateDocumentWS),
        tap(({ payload }) => this.documentsService.updateDocumentWS(payload))
      ),
    { dispatch: false }
  );

  delete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentActions.deleteDocumentWS),
        tap(({ payload }) => this.documentsService.deleteDocumentWS(payload))
      ),
    { dispatch: false }
  );

  uploadDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.uploadDocuments),
      mergeMap(({ payload }) =>
        this.documentsService.uploadDocuments(payload.data, payload.attr).pipe(
          filter((event: any) => event.type === HttpEventType.Response),
          map((event: HttpResponse<{ res: DocNode } | DocNode>) => {
            const body = event.body;
            const doc =
              body && typeof body === 'object' && 'res' in body
                ? (body as { res: DocNode }).res
                : (body as DocNode);
            const node: DocNode = {
              ...(doc || {}),
              attr: payload.attr,
            } as DocNode;
            return DocumentActions.uploadDocumentsSuccess({
              payload: [node],
            });
          }),
          catchError(({ sts }) =>
            of(DocumentActions.uploadDocumentsFailure({ payload: sts }))
          )
        )
      )
    )
  );

  preUploadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.preUploadDocument),
      mergeMap(({ payload }) => {
        const { data, file } = payload;
        return this.documentsService.preUploadDocument(data).pipe(
          map(({ res }) => {
            return DocumentActions.preUploadDocumentSuccess({
              payload: { res, file },
            });
          }),
          catchError(({ sts }) =>
            of(DocumentActions.preUploadDocumentFailure({ payload: sts }))
          )
        );
      })
    )
  );

  preUploadDocument2Stage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentActions.preUploadDocumentSuccess),
        mergeMap(({ payload }) => {
          const { res, file } = payload;

          const formData: FormData = new FormData();

          formData.append('data', file);
          formData.append('tkn', res.tkn);

          let timestamp = Date.now();
          let lastLoaded = 0;

          return this.documentsService.uploadDocuments(formData).pipe(
            takeUntil(
              this.store.select(selectPreUploadDocuments).pipe(
                map(docs => docs.find(doc => doc.tkn === res?.tkn)),
                filter(
                  (doc: PreUploadState) =>
                    doc.status === DOCUMENT_UPLOAD_CANCELED
                )
              )
            ),
            filter(
              (event: any) =>
                event.type === HttpEventType.Response ||
                event.type === HttpEventType.UploadProgress ||
                event.type === HttpEventType.Sent
            ),
            map(
              (
                event: HttpResponse<DocNode> | HttpProgressEvent | HttpSentEvent
              ) => {
                switch (event.type) {
                  case HttpEventType.Sent:
                    this.store.dispatch(
                      DocumentActions.preUploadDocumentUpdate({
                        payload: {
                          tkn: res.tkn,
                          toUpdate: {
                            status: DOCUMENT_UPLOAD_STARTED,
                          },
                        },
                      })
                    );
                    return of(null);
                  case HttpEventType.UploadProgress: {
                    const loaded = event.loaded;
                    const time = Date.now() - timestamp;
                    const percent = (loaded - lastLoaded) / 100;
                    const chunk = percent * file.size;
                    const speed = (chunk / (time / 1000)).toFixed(2);

                    timestamp = Date.now();
                    lastLoaded = loaded;

                    this.store.dispatch(
                      DocumentActions.preUploadDocumentUpdate({
                        payload: {
                          tkn: res.tkn,
                          toUpdate: {
                            progress: Math.round(
                              (100 * event.loaded) / file.size
                            ),
                            speed: parseFloat(speed),
                          },
                        },
                      })
                    );
                    return of(null);
                  }
                  case HttpEventType.Response:
                    this.store.dispatch(
                      DocumentActions.preUploadDocumentUpdate({
                        payload: {
                          tkn: res.tkn,
                          toUpdate: {
                            status: DOCUMENT_UPLOAD_COMPLETED,
                          },
                        },
                      })
                    );
                    this.store.dispatch(
                      DocumentActions.updateDocumentWSSuccess({
                        payload: event.body,
                      })
                    );
                    return of(null);

                  default:
                    return of(null);
                }
              }
            ),
            catchError(() => {
              this.store.dispatch(
                DocumentActions.preUploadDocumentUpdate({
                  payload: {
                    tkn: res.tkn,
                    toUpdate: { status: DOCUMENT_UPLOAD_CANCELED },
                  },
                })
              );
              this.store.dispatch(
                DocumentActions.preUploadDocumentDeleteAllByIds({
                  payload: res.docIds || [],
                })
              );
              const docId = res.docIds?.[0];
              if (docId && res.refId) {
                this.store.dispatch(
                  DocumentActions.deleteDocumentWS({
                    payload: { id: `${res.refId}#${docId}` },
                  })
                );
              }
              return of(null);
            })
          );
          // Number of concurrent uploads
        }, 5)
      ),
    { dispatch: false }
  );

  deleteDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.deleteDocument),
      mergeMap(({ payload }) =>
        this.documentsService.deleteDocument(payload.docId, payload.refId).pipe(
          map(() => DocumentActions.deleteDocumentSuccess({ payload })),
          catchError(({ sts }) =>
            of(DocumentActions.deleteDocumentFailure({ payload: sts }))
          )
        )
      )
    )
  );

  getEntityDocumentsWs$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentActions.getEntityDocumentsWS),
        tap(({ payload }) => this.documentsService.getItemDocumentsWs(payload))
      ),
    { dispatch: false }
  );

  getEntityDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.getEntityDocuments),
      withLatestFrom(this.store.select(selectCurrentPerson)),
      mergeMap(([action, person]) => {
        const refId = action.payload?.refId || `prs=${person.prsId}`;

        return this.documentsService
          .getDocumentsOfEntity(refId, action.payload?.type)
          .pipe(
            map(({ res }) =>
              DocumentActions.getEntityDocumentsSuccess({ payload: res })
            ),
            catchError(() => of(DocumentActions.getEntityDocumentsFailure()))
          );
      })
    )
  );

  createPlaceholder$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentActions.createDocumentPlaceholderWS),
        tap(({ payload }) =>
          this.documentsService.createDocumentPlaceholderWS(payload)
        )
      ),
    { dispatch: false }
  );

  generatePlaceholderToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.generatePlaceholderToken),
      mergeMap(({ payload }) => {
        return this.documentsService.generatePlaceholderToken(payload).pipe(
          map(({ res }) => {
            return DocumentActions.generatePlaceholderTokenSuccess({
              payload: res,
            });
          }),
          catchError(({ sts }) =>
            of(
              DocumentActions.generatePlaceholderTokenFailure({ payload: sts })
            )
          )
        );
      })
    )
  );

  getCreatorLogos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.getLogos),
      mergeMap(() =>
        this.documentsService.getLogoTemplates().pipe(
          map(({ res }) => DocumentActions.getLogosSuccess({ payload: res })),
          catchError(() => of(DocumentActions.getEntityDocumentsFailure()))
        )
      )
    )
  );

  updateLogo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.updateCompanyLogo),
      mergeMap(({ payload }) =>
        this.documentsService.updateLogo(payload).pipe(
          map(({ res }) =>
            DocumentActions.updateCompanyLogoSuccess({ payload: res })
          ),
          catchError(({ sts }) =>
            of(DocumentActions.updateCompanyLogoFailure({ payload: sts }))
          )
        )
      )
    )
  );

  sendDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.sendDocument),
      mergeMap(({ payload }) =>
        this.documentsService
          .sendDocument(payload.id, payload.destination)
          .pipe(
            map(({ res }) =>
              DocumentActions.sendDocumentSuccess({
                payload: { response: res, id: payload.id },
              })
            ),
            catchError(({ sts }) =>
              of(DocumentActions.sendDocumentFailure({ payload: sts }))
            )
          )
      )
    )
  );

  updateDocDescription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.updateDocumentDescription),
      mergeMap(({ payload }) =>
        this.documentsService
          .updateDocumentDescription(payload.id, payload.description)
          .pipe(
            map(({ res }) =>
              DocumentActions.updateDocumentDescriptionSuccess({
                payload: { description: res, id: payload.id },
              })
            ),
            catchError(({ sts }) =>
              of(
                DocumentActions.updateDocumentDescriptionFailure({
                  payload: sts,
                })
              )
            )
          )
      )
    )
  );

  bulkDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.bulkDelete),
      mergeMap(({ payload }) =>
        this.documentsService.bulkDelete(payload).pipe(
          map(response => {
            return DocumentActions.bulkDeleteSuccess({
              payload: ((response as any).res as any[]).map(item => item._id),
            });
          }),
          catchError(error =>
            of(DocumentActions.bulkDeleteFailure({ payload: error }))
          )
        )
      )
    )
  );

  submitSelectedTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentActions.applyTemplate),
      mergeMap(({ payload }) =>
        this.documentsService
          .submitSelectedTemplate(payload.templateData, payload.dealId)
          .pipe(
            map(response => {
              return DocumentActions.applyTemplateSuccess({
                payload: response,
              });
            }),
            catchError(error =>
              of(
                DocumentActions.applyTemplateFailure({
                  payload: error,
                })
              )
            )
          )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private documentsService: DocumentsService,
    private store: Store<AppState>
  ) {}
}
